import { Activity } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useLiveStatus } from '@/components/live-status-context'
import { cn } from '@/lib/utils'
import type { SSEState } from '@/lib/sse'

type Tone = {
  label: string
  short: string
  description: string
  dot: string
  text: string
  pulse: boolean
}

const TONES: Record<SSEState, Tone> = {
  open: {
    label: 'Live',
    short: 'Live',
    description: 'Live updates active.',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
    text: 'text-emerald-600 dark:text-emerald-300',
    pulse: true,
  },
  connecting: {
    label: 'Connecting…',
    short: 'Connecting',
    description: 'Connecting to live updates…',
    dot: 'bg-amber-500 dark:bg-amber-400',
    text: 'text-amber-600 dark:text-amber-300',
    pulse: true,
  },
  reconnecting: {
    label: 'Reconnecting…',
    short: 'Reconnecting',
    description: 'Stream interrupted — retrying with backoff.',
    dot: 'bg-amber-500 dark:bg-amber-400',
    text: 'text-amber-600 dark:text-amber-300',
    pulse: true,
  },
  closed: {
    label: 'Offline',
    short: 'Offline',
    description: 'Not subscribed. Open Runs or a run detail to start streaming.',
    dot: 'bg-zinc-400 dark:bg-zinc-500',
    text: 'text-muted-foreground',
    pulse: false,
  },
}

export function LiveIndicator() {
  const state = useLiveStatus()
  const tone = TONES[state]
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11.5px] font-medium ring-1 ring-inset ring-border/70 bg-background/60',
            'select-none',
            tone.text,
          )}
          aria-live="polite"
        >
          <span className="relative inline-flex">
            <span
              className={cn(
                'inline-flex size-1.5 rounded-full',
                tone.dot,
                tone.pulse && 'shadow-[0_0_0_3px_currentColor]/0',
              )}
            />
            {tone.pulse ? (
              <span
                aria-hidden
                className={cn(
                  'absolute inset-0 inline-flex animate-ping rounded-full opacity-60',
                  tone.dot,
                )}
              />
            ) : null}
          </span>
          <span className="hidden sm:inline">{tone.label}</span>
          <span className="sm:hidden">
            <Activity className="size-3.5" />
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[14rem]">
        <div className="font-medium">{tone.short}</div>
        <div className="text-[11px] text-muted-foreground">{tone.description}</div>
      </TooltipContent>
    </Tooltip>
  )
}
